import React from 'react';
import AmountCell from 'pages/Dashboard/pages/Billing/Payments/AmountCell';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  formatDate,
} from 'utils/date';
import {
  MoveToCreditCurrencyCell,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/EditableInputs';
import {
  EncounterWithPayment,
} from 'views/EMR/Billing/AdjustPayment';

const encounterColumn: ColumnDef<EncounterWithPayment> = {
  id: 'encounter',
  accessorKey: 'encounterId',
  columnTitle: 'Encounter',
  minSize: 150,
  cell: ({ row }) => (
    row.original?.isCredit ? (
      <div className="gap-px flex flex-col font-semibold max-w-max">
        <span className="text-gray-500">
          Credit
        </span>
      </div>
    ) : (
      <div className="gap-px flex flex-col font-semibold max-w-max">
        <span className="text-gray-500">
          {formatDate(row.original?.startDateTime ?? '')}
        </span>
        <small className="truncate inline-block max-w-fit">
          {row.original?.typeOfVisitDescription ?? '--'}
        </small>
      </div>
    )
  ),
};

const patientResponsibilityColumn: ColumnDef<EncounterWithPayment> = {
  id: 'patientResponsibility',
  accessorKey: 'patientResponsibility',
  columnTitle: (
    <>
      Patient
      <br />
      Responsibility
    </>),
  multiLineHeader: true,
  cell: ({ row, column }) => (
    row.original?.isCredit ? (
      <span className="text-gray-500">—</span>
    ) : (
      <AmountCell<EncounterWithPayment>
        value={row.original?.patientResponsibility ?? 0}
        columnDef={column.columnDef}
        showZero
        showNegative={false}
      />
    )
  ),
};

const patientPaidColumn: ColumnDef<EncounterWithPayment> = {
  id: 'totalPatientPaid',
  accessorKey: 'totalPatientPaid',
  columnTitle: 'Patient Paid',
  cell: ({ row, column }) => (
    <AmountCell<EncounterWithPayment>
      value={row.original?.totalPatientPaid ?? 0}
      columnDef={column.columnDef}
      showZero
      showNegative={false}
    />
  ),
};

const patientPaidBySquareColumn: ColumnDef<EncounterWithPayment> = {
  id: 'totalPatientPaid',
  accessorKey: 'totalPatientPaid',
  columnTitle: (
    <>
      Patient Paid
      <br />
      By Square
      {' '}
    </>),
  multiLineHeader: true,
  cell: ({ row, column }) => (
    <AmountCell<EncounterWithPayment>
      value={row.original?.netSquarePaidAmount ?? 0}
      columnDef={column.columnDef}
      showZero
      showNegative={false}
    />
  ),
};

const patientBalanceColumn: ColumnDef<EncounterWithPayment> = {
  id: 'patientBalance',
  accessorKey: 'patientBalance',
  columnTitle: 'Patient Balance',
  cell: ({ row, column }) => (row.original?.isCredit ? (
    <span className="text-gray-500">—</span>
  ) : (
    <AmountCell<EncounterWithPayment>
      value={row.original?.patientBalance ?? 0}
      columnDef={column.columnDef}
      showZero
      showNegative={false}
    />
  )),
};

const moveToCreditAmountColumn: ColumnDef<EncounterWithPayment> = {
  id: 'moveToCreditAmount',
  accessorKey: 'moveToCreditAmount',
  columnTitle: 'Amount to Move',
  className: '!border !rounded-sm !border-gray-200 !py-0',
  cell: ({ row, column, ...props }) => (
    (row.original?.totalPatientPaid ?? 0) === 0
      ? (
        <AmountCell<EncounterWithPayment>
          value={undefined}
          columnDef={column.columnDef}
          showZero
        />
      )
      : (
        <MoveToCreditCurrencyCell
          row={row}
          column={column}
          {...props}
        />
      )
  ),
};

const refundAmountColumn: ColumnDef<EncounterWithPayment> = {
  id: 'refundAmount',
  accessorKey: 'refundAmount',
  columnTitle: 'Refund Amount',
  className: '!border !rounded-sm !border-gray-200 !py-0',
  cell: ({ row, column, ...props }) => (
    (row.original?.totalPatientPaid ?? 0) === 0
      ? (
        <AmountCell<EncounterWithPayment>
          value={undefined}
          columnDef={column.columnDef}
          showZero
        />
      )
      : (
        <MoveToCreditCurrencyCell
          row={row}
          column={column}
          {...props}
        />
      )
  ),
};

export const moveToCreditColumns: ColumnDef<EncounterWithPayment>[] = [
  encounterColumn,
  patientResponsibilityColumn,
  patientPaidColumn,
  patientBalanceColumn,
  moveToCreditAmountColumn,
];

export const refundColumns: ColumnDef<EncounterWithPayment>[] = [
  encounterColumn,
  patientResponsibilityColumn,
  patientPaidColumn,
  patientBalanceColumn,
  refundAmountColumn,
];

export const squareRefundColumns: ColumnDef<EncounterWithPayment>[] = [
  encounterColumn,
  patientResponsibilityColumn,
  patientPaidBySquareColumn,
  patientBalanceColumn,
  refundAmountColumn,
];

export const getColumns = (
  adjustmentType: number,
  isSquare: boolean = false,
) => {
  if (adjustmentType === 0) {
    return moveToCreditColumns;
  }

  if (isSquare) {
    return squareRefundColumns;
  }

  return refundColumns;
};
